import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { MatExpansionModule } from '@angular/material/expansion';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  AuditService,
  AuditTimelineEntry,
  AuditTypedValue,
  AuditAction,
  RelatedNode,
  RelatedRecordsTree,
} from '../../services/audit.service';
import { AuditFeedStateService } from '../../services/audit-feed-state.service';

/** Entity types that support a related-records panel on the drill-down. */
const RELATED_ENTITY_TYPES = ['project', 'site', 'proposal'];

@Component({
  standalone: true,
  selector: 'app-audit-timeline',
  imports: [
    CommonModule,
    MatExpansionModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatDividerModule,
  ],
  providers: [CurrencyPipe, DatePipe],
  templateUrl: './audit-timeline.component.html',
  styleUrl: './audit-timeline.component.scss',
})
export class AuditTimelineComponent implements OnInit, OnDestroy {
  entityType = '';
  entityId = '';
  highlightedCommitId: string | null = null;

  entries: AuditTimelineEntry[] = [];
  loading = false;
  error = false;

  relatedTree: RelatedRecordsTree | null = null;
  relatedLoading = false;

  // Node-agnostic expansion state: every related node (any type, with or without children) can
  // expand in place to reveal its own change history and nested records. Keyed by nodeKey().
  expandedNodeKeys = new Set<string>();

  // Lazily-loaded change history per node, keyed by nodeKey(). Populated on first expand and
  // retained so re-expanding a node never refetches.
  historyCache = new Map<string, { loading: boolean; error: boolean; entries: AuditTimelineEntry[] }>();

  private destroy$ = new Subject<void>();

  constructor(
    private auditService: AuditService,
    private route: ActivatedRoute,
    private router: Router,
    private currencyPipe: CurrencyPipe,
    private datePipe: DatePipe,
    private auditFeedState: AuditFeedStateService,
  ) {}

  ngOnInit(): void {
    // Subscribe to paramMap (not snapshot) so navigating between drill-downs — e.g. clicking a
    // related record on a project's page — reloads for the new entity. Angular reuses this component
    // instance across same-route param changes, so ngOnInit fires only once; the snapshot alone would
    // leave stale data on the page. Reset per-entity state on each navigation before reloading.
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.entityType = params.get('entityType') ?? '';
        this.entityId = params.get('entityId') ?? '';
        this.highlightedCommitId = this.route.snapshot.queryParamMap.get('commit');
        this.entries = [];
        this.relatedTree = null;
        this.expandedNodeKeys.clear();
        this.historyCache.clear();
        this.error = false;
        this.loadTimeline();
        this.loadRelated();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goBack(): void {
    // Only ever trust a stored *dashboard* URL. The captured return URL is
    // in-memory and always a dashboard URL when set by the dashboard's row click,
    // but guarding the prefix keeps a stale/unexpected value from routing the user
    // somewhere surprising — fall back to a clean dashboard instead.
    const returnUrl = this.auditFeedState.getReturnUrl();
    if (returnUrl && returnUrl.startsWith('/internal/dashboard')) {
      this.router.navigateByUrl(returnUrl);
    } else {
      this.router.navigate(['/internal/dashboard']);
    }
  }

  chipClass(action: AuditAction): string {
    switch (action) {
      case 'CREATED': return 'chip-created';
      case 'UPDATED': return 'chip-updated';
      case 'DELETED': return 'chip-deleted';
    }
  }

  /** CSS class for the timeline node dot, keyed by action. */
  nodeClass(action: AuditAction): string {
    switch (action) {
      case 'CREATED': return 'node-created';
      case 'UPDATED': return 'node-updated';
      case 'DELETED': return 'node-deleted';
    }
  }

  /**
   * Returns true when the user string looks like a service account.
   * Heuristic: no space in name (single token) OR contains "API".
   */
  isServiceAccount(user: string): boolean {
    return !user.includes(' ') || /api/i.test(user);
  }

  /**
   * Returns 1–2 initials for the avatar bubble.
   * "CREtelligent API" → "CA", "Rainier Dela Cruz" → "RD".
   */
  initials(user: string): string {
    const parts = user.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  /**
   * CSS class for a change value cell.
   * 'old'   → struck-through muted text
   * 'new'   → navy bold
   * 'added' → green bold (old was null, new is set — newly added field)
   * 'empty' → placeholder dash styling
   */
  valueClass(val: AuditTypedValue | null, side: 'old' | 'new', otherSide: AuditTypedValue | null): string {
    if (val === null) return 'val-empty';
    if (side === 'new' && otherSide === null) return 'val-added'; // first-set field
    if (side === 'old') return 'val-old';
    return 'val-new';
  }

  /**
   * Returns true when the value is an ENUM type — used to wrap display in a pill.
   */
  isEnum(val: AuditTypedValue | null): boolean {
    return val !== null && val.type === 'ENUM';
  }

  /**
   * Format an AuditTypedValue for display in the changes table.
   * Returns "—" for a null side (create/delete).
   */
  formatValue(val: AuditTypedValue | null): string {
    if (val === null) return '—';
    switch (val.type) {
      case 'CURRENCY':
        return this.currencyPipe.transform(val.raw, 'USD', 'symbol', '1.2-2') ?? val.display;
      case 'DATE':
        return this.datePipe.transform(val.raw, 'mediumDate') ?? val.display;
      case 'BOOLEAN':
        return val.raw ? 'Yes' : 'No';
      default:
        return val.display;
    }
  }

  hasChanges(entry: AuditTimelineEntry): boolean {
    return entry.changes && entry.changes.length > 0;
  }

  /** True when the current entity type supports the related-records panel. */
  get showRelatedPanel(): boolean {
    return RELATED_ENTITY_TYPES.includes(this.entityType.toLowerCase());
  }

  /** Stable key for a related node's expansion + history cache entries. */
  nodeKey(node: RelatedNode): string {
    return `${node.entityType}:${node.entityId}`;
  }

  /**
   * Expands/collapses a related-records node in place. Every node type is expandable now
   * (not just those with children). On the first expand of a node we lazily fetch its change
   * history and cache it; re-expanding a cached node never refetches.
   */
  toggleNode(node: RelatedNode): void {
    const key = this.nodeKey(node);
    if (this.expandedNodeKeys.has(key)) {
      this.expandedNodeKeys.delete(key);
      return;
    }
    this.expandedNodeKeys.add(key);
    if (!this.historyCache.has(key)) {
      this.loadNodeHistory(node, key);
    }
  }

  isExpanded(node: RelatedNode): boolean {
    return this.expandedNodeKeys.has(this.nodeKey(node));
  }

  /** Cached change-history entries for a node (empty until loaded). */
  historyEntries(node: RelatedNode): AuditTimelineEntry[] {
    return this.historyCache.get(this.nodeKey(node))?.entries ?? [];
  }

  isHistoryLoading(node: RelatedNode): boolean {
    return this.historyCache.get(this.nodeKey(node))?.loading ?? false;
  }

  isHistoryError(node: RelatedNode): boolean {
    return this.historyCache.get(this.nodeKey(node))?.error ?? false;
  }

  /** Navigates to the drill-down (full timeline) for a related record. */
  openRecord(node: RelatedNode): void {
    this.router.navigate(['/internal/audit', node.entityType, node.entityId]);
  }

  /**
   * Fetches a node's change history once, storing loading/error/success state in historyCache.
   * A failure is non-fatal — we log and surface an inline error rather than throwing.
   */
  private loadNodeHistory(node: RelatedNode, key: string): void {
    this.historyCache.set(key, { loading: true, error: false, entries: [] });
    this.auditService
      .getTimeline(node.entityType, node.entityId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (entries) => {
          this.historyCache.set(key, { loading: false, error: false, entries });
        },
        error: (err) => {
          console.error('Failed to load node history', err);
          this.historyCache.set(key, { loading: false, error: true, entries: [] });
        },
      });
  }

  /**
   * Loads the related-records tree for project/site entities. A failure here
   * is non-fatal — it just hides the panel — so we log and move on rather
   * than surfacing a dedicated error state.
   */
  private loadRelated(): void {
    if (!this.showRelatedPanel || !this.entityId) return;

    this.relatedLoading = true;
    this.auditService
      .getRelated(this.entityType, this.entityId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tree) => {
          this.relatedTree = tree;
          this.relatedLoading = false;
        },
        error: (err) => {
          console.error('Failed to load related records', err);
          this.relatedTree = null;
          this.relatedLoading = false;
        },
      });
  }

  private loadTimeline(): void {
    if (!this.entityType || !this.entityId) {
      this.error = true;
      return;
    }

    this.loading = true;
    this.error = false;

    this.auditService
      .getTimeline(this.entityType, this.entityId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.entries = data;
          this.loading = false;
          if (this.highlightedCommitId) {
            setTimeout(() => {
              document
                .getElementById('event-' + this.highlightedCommitId)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
          }
        },
        error: () => {
          this.entries = [];
          this.loading = false;
          this.error = true;
        },
      });
  }
}
